import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface UpgradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: string;
  targetPlan: string;
  trialDaysRemaining: number;
  loading: boolean;
}

export function UpgradeConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  targetPlan,
  trialDaysRemaining,
  loading,
}: UpgradeConfirmationModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Plan Upgrade</DialogTitle>
          <DialogDescription>
            You're about to change your subscription plan
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-start gap-3 p-4 bg-warning-surface border border-warning-border rounded-lg mb-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-warning-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-warning-heading mb-1">
                Your trial will end immediately
              </p>
              <p className="text-sm text-warning-foreground-deep">
                By upgrading now, you'll lose the remaining {trialDaysRemaining}{' '}
                {trialDaysRemaining === 1 ? 'day' : 'days'} of your trial and billing will start today.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground">Current plan:</span>
              <span className="text-sm font-medium">{currentPlan}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-muted-foreground">New plan:</span>
              <span className="text-sm font-semibold">{targetPlan}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Upgrading...
              </>
            ) : (
              `Upgrade to ${targetPlan}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
